import React from 'react';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import { EntityType } from './types';
import { EntityView } from './components/EntityView';
import { AttributeManagement } from './components/AttributeManagement';

import { EventsView } from './components/EventsView';
import ParserLogsView from './components/ParserLogsView';
import { AdminUsersPage } from './components/AdminUsersPage';
import { Dashboard } from './components/Dashboard';
import { FilteredView } from './components/FilteredView';
import { VIEW_REGISTRY } from './config/viewsConfig';
import { ThemeProvider } from './ThemeContext';
import { EditModeProvider } from './context/EditModeContext';
import { LabProvider } from './LabContext';
import { TopNav } from './components/TopNav';
import './App.css';

// NOTE: BrowserRouter lives in index.tsx. To silence React Router v7
// deprecation warnings, add future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
// to the <BrowserRouter> in that file.

const ENTITY_TYPES = ['sample', 'test', 'assay', 'order'] as const;

function EntityViewWrapper() {
  const location = useLocation();

  const pathParts = location.pathname.split('/').filter(p => p);
  const entityTypeFromPath = pathParts[0] && (ENTITY_TYPES as readonly string[]).includes(pathParts[0])
    ? pathParts[0] as EntityType
    : null;

  const validEntityType: EntityType = entityTypeFromPath || 'sample';

  return <EntityView entityType={validEntityType} />;
}

function AttributeManagementWrapper() {
  const location = useLocation();

  const pathParts = location.pathname.split('/').filter(p => p);
  const entityTypeFromPath = pathParts[0] && (ENTITY_TYPES as readonly string[]).includes(pathParts[0])
    ? pathParts[0] as EntityType
    : null;

  const validEntityType: EntityType = entityTypeFromPath || 'sample';

  return <AttributeManagement entityType={validEntityType} />;
}

function FilteredViewWrapper() {
  const { viewId } = useParams<{ viewId: string }>();
  const navigate = useNavigate();

  const viewDef = viewId ? VIEW_REGISTRY[viewId] : undefined;

  if (!viewDef) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        <h2>View not found</h2>
        <p>No view registered with ID "{viewId}".</p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            background: 'var(--accent-blue)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return <FilteredView view={viewDef} />;
}

function NotFound() {
  const navigate = useNavigate();
  return (
    <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
      <h2>Page not found</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>The page you're looking for doesn't exist.</p>
      <button onClick={() => navigate('/')} className="back-btn">Go to Dashboard</button>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <EditModeProvider>
        <LabProvider>
          <div className="App">
            <TopNav />
            <main className="app-main">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                {ENTITY_TYPES.map(et => (
                  <React.Fragment key={et}>
                    <Route path={`/${et}`} element={<EntityViewWrapper />} />
                    <Route path={`/${et}/:id`} element={<EntityViewWrapper />} />
                    <Route path={`/${et}/attributes`} element={<AttributeManagementWrapper />} />
                  </React.Fragment>
                ))}
                <Route path="/views/:viewId" element={<FilteredViewWrapper />} />
                <Route path="/events" element={<EventsView />} />
                <Route path="/parser-logs" element={<ParserLogsView />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </div>
        </LabProvider>
      </EditModeProvider>
    </ThemeProvider>
  );
}

export default App;
